import { get } from 'lodash';
import { eventChannel } from 'redux-saga';
import { all, fork, join, put, takeEvery } from 'typed-redux-saga/macro';

import { actionKeyboardEvent } from './actions';

export default function* keyboardSagas() {
  yield* all([fork(initKeyboardEventListener)]);
}

function* initKeyboardEventListener() {
  const chan = eventChannel<KeyboardEvent>((emitter) => {
    const handleKeyEvt = (evt: KeyboardEvent) => {
      // The spring-loaded text tool's RELEASE ("t" keyup) has to reach the saga
      // even when it lands on a focused input: hold-T + click places a box and
      // autofocuses its text field, so the release almost always targets that
      // field. Dropping it here left `textToolHeld` stuck true, which both
      // stranded the editor in text mode and made every later hold a no-op
      // (engageTextOverlayTool early-returns while it is set) — VE-25637.
      // Letting it through is safe: releaseTextOverlayTool no-ops unless a hold
      // actually engaged the tool.
      const isTextToolRelease =
        evt.type === 'keyup' && (evt.key === 't' || evt.key === 'T');
      if (
        evt.defaultPrevented ||
        (!isTextToolRelease &&
          ['INPUT', 'TEXTAREA'].includes(
            get(evt, 'target.tagName', '').toUpperCase()
          )) ||
        (evt.code === 'Escape' &&
          evt.target instanceof Element &&
          evt.target.matches('.video-react-menu-button-popup'))
      ) {
        return;
      }

      if (
        evt.code === 'Space' ||
        (evt.altKey && (evt.code === 'KeyS' || evt.code === 'KeyR'))
      ) {
        evt.preventDefault();
      }

      emitter(evt);
    };

    window.addEventListener('keydown', handleKeyEvt);
    window.addEventListener('keyup', handleKeyEvt);
    return () => {
      window.removeEventListener('keydown', handleKeyEvt);
      window.removeEventListener('keyup', handleKeyEvt);
    };
  });

  const relay = yield* takeEvery(chan, function* (evt) {
    yield* put(
      actionKeyboardEvent({
        key: evt.key,
        altKey: evt.altKey,
        ctrlKey: evt.ctrlKey,
        metaKey: evt.metaKey,
        shiftKey: evt.shiftKey,
        type: evt.type === 'keyup' ? 'keyup' : 'keydown',
      })
    );
  });

  // takeEvery never closes its channel; close it here so the window listeners
  // are removed when this saga is cancelled or crashes (keepAlive restarts it).
  try {
    yield* join(relay);
  } finally {
    chan.close();
  }
}
